import { useRef, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { GameBoxApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";

const prizeSchema = z.object({
  prizeCode: z.string().min(1, "Prize code is required"),
  prizeName: z.string().min(1, "Prize name is required"),
  prizeType: z.string().min(1, "Prize type is required"),
  prizeValue: z.string().min(1, "Prize value is required"),
  perUserLimit: z.string().min(1, "Per user limit is required"),
  isActive: z.boolean().default(true),
  winProbability: z.string().min(1, "Win probability is required"),
});

const mysteryBoxFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  winBehavior: z.enum(["guaranteed", "nearMiss"]),
  useDefault: z.boolean().default(true),
  displayText: z.string().min(1, "Display text is required"),
  termsText: z.string().min(1, "Terms text is required"),
  prizes: z.array(prizeSchema).min(1, "At least one prize is required"),
});

type MysteryBoxFormValues = z.infer<typeof mysteryBoxFormSchema>;

const defaultPrize = () => ({
  prizeCode: "",
  prizeName: "",
  prizeType: "Points",
  prizeValue: "",
  perUserLimit: "1",
  isActive: true,
  winProbability: "",
});

const defaultFormValues: MysteryBoxFormValues = {
  title: "",
  winBehavior: "guaranteed",
  useDefault: true,
  displayText: "Open the box and see the magic",
  termsText: "Terms & Conditions",
  prizes: [defaultPrize()],
};

const getUserId = () => {
  try {
    const userInfo = sessionStorage.getItem("userInfo");
    return userInfo ? (JSON.parse(userInfo).userId ?? 1) : 1;
  } catch {
    return 1;
  }
};

interface CreateMysteryBoxFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function CreateMysteryBoxForm({
  onSuccess,
  onCancel,
}: CreateMysteryBoxFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<MysteryBoxFormValues>({
    resolver: zodResolver(mysteryBoxFormSchema),
    defaultValues: defaultFormValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "prizes",
  });

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImageFile(file);
    setImageError(null);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    form.reset(defaultFormValues);
    clearImage();
  };

  const onSubmit = async (data: MysteryBoxFormValues) => {
    if (!imageFile) {
      setImageError("Box image is required");
      return;
    }

    setIsSubmitting(true);
    try {
      const userId = getUserId();

      const response = await GameBoxApi.insertUpdateGameBoxConfig({
        file: imageFile,
        commonJson: {
          gameBox: {
            p_Id: 0,
            p_Title: data.title,
            p_ImageUrl: "",
            p_GuaranteedWinFlag: data.winBehavior === "guaranteed",
            p_ShowNearMiss: data.winBehavior === "nearMiss",
            p_DisplayText: data.displayText,
            p_TermsText: data.termsText,
            p_CreatedBy: userId,
            p_UpdatedBy: userId,
            p_UseDefault: data.useDefault,
            p_ClientId: 1009,
          },
          ListPrizes: data.prizes.map((prize) => ({
            PrizeCode: prize.prizeCode,
            PrizeName: prize.prizeName,
            PrizeType: prize.prizeType,
            PrizeValue: parseInt(prize.prizeValue, 10),
            PerUserLimit: parseInt(prize.perUserLimit, 10),
            IsActive: prize.isActive,
            WinProbability: parseInt(prize.winProbability, 10),
          })),
        },
      });

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: response.message || "Mystery box created successfully",
        });
        resetForm();
        onSuccess?.();
      }
    } catch (error: unknown) {
      console.error("Error creating mystery box:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to create mystery box",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Box Details</h3>

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter mystery box title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <FormLabel>Box Image</FormLabel>
            <div className="flex items-start gap-4">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Box preview"
                    className="h-32 w-32 rounded-lg border object-cover"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -right-2 -top-2 h-6 w-6"
                    onClick={clearImage}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="mystery-box-image"
                  className={`flex h-32 w-48 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                    imageError
                      ? "border-red-300 bg-red-50 hover:border-red-400"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <Upload
                    className={`h-8 w-8 ${
                      imageError ? "text-red-400" : "text-gray-400"
                    }`}
                  />
                  <span
                    className={`mt-2 px-2 text-center text-xs font-medium ${
                      imageError ? "text-red-600" : "text-gray-600"
                    }`}
                  >
                    Click to upload image
                  </span>
                </label>
              )}
            </div>
            <Input
              ref={fileInputRef}
              id="mystery-box-image"
              type="file"
              accept=".jpeg,.jpg,.png,.gif,.bmp,.tiff,image/jpeg,image/png,image/gif,image/bmp,image/tiff"
              onChange={handleImageChange}
              className="hidden"
            />
            {imageError && (
              <p className="text-sm font-medium text-destructive">
                {imageError}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              Supported formats: JPEG, PNG, GIF, BMP, TIFF
            </p>
          </div>

          <FormField
            control={form.control}
            name="winBehavior"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel>Win Behavior</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="grid grid-cols-1 gap-4 md:grid-cols-2"
                  >
                    <label
                      htmlFor="guaranteed-win"
                      className={`flex cursor-pointer items-start space-x-3 rounded-md border p-4 ${
                        field.value === "guaranteed"
                          ? "border-primary bg-primary/5"
                          : ""
                      }`}
                    >
                      <RadioGroupItem
                        value="guaranteed"
                        id="guaranteed-win"
                        className="mt-0.5"
                      />
                      <div className="space-y-1 leading-none">
                        <span className="text-sm font-medium">
                          Guaranteed Win
                        </span>
                        <p className="text-sm text-muted-foreground">
                          Every user is guaranteed to win a prize.
                        </p>
                      </div>
                    </label>

                    <label
                      htmlFor="show-near-miss"
                      className={`flex cursor-pointer items-start space-x-3 rounded-md border p-4 ${
                        field.value === "nearMiss"
                          ? "border-primary bg-primary/5"
                          : ""
                      }`}
                    >
                      <RadioGroupItem
                        value="nearMiss"
                        id="show-near-miss"
                        className="mt-0.5"
                      />
                      <div className="space-y-1 leading-none">
                        <span className="text-sm font-medium">
                          Show Near Miss
                        </span>
                        <p className="text-sm text-muted-foreground">
                          Display near-miss animation for losing picks.
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="useDefault"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) =>
                      field.onChange(Boolean(checked))
                    }
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Use Default</FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Use default settings for this mystery box.
                  </p>
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="displayText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Display Text</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Open the box and see the magic"
                    rows={2}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="termsText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Terms Text</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Terms & Conditions"
                    rows={2}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Prizes</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => append(defaultPrize())}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Prize
            </Button>
          </div>

          {fields.map((field, index) => (
            <div key={field.id} className="space-y-4 rounded-md border p-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">
                  Prize {index + 1}
                </h4>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(index)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name={`prizes.${index}.prizeCode`}
                  render={({ field: prizeField }) => (
                    <FormItem>
                      <FormLabel>Prize Code</FormLabel>
                      <FormControl>
                        <Input placeholder="PM005" {...prizeField} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`prizes.${index}.prizeName`}
                  render={({ field: prizeField }) => (
                    <FormItem>
                      <FormLabel>Prize Name</FormLabel>
                      <FormControl>
                        <Input placeholder="100 Points" {...prizeField} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`prizes.${index}.prizeType`}
                  render={({ field: prizeField }) => (
                    <FormItem>
                      <FormLabel>Prize Type</FormLabel>
                      <Select
                        onValueChange={prizeField.onChange}
                        value={prizeField.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select prize type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Points">Points</SelectItem>
                          <SelectItem value="Discount">Discount</SelectItem>
                          <SelectItem value="Cash">Cash</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`prizes.${index}.prizeValue`}
                  render={({ field: prizeField }) => (
                    <FormItem>
                      <FormLabel>Prize Value</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="100"
                          {...prizeField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`prizes.${index}.perUserLimit`}
                  render={({ field: prizeField }) => (
                    <FormItem>
                      <FormLabel>Per User Limit</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          placeholder="1"
                          {...prizeField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`prizes.${index}.winProbability`}
                  render={({ field: prizeField }) => (
                    <FormItem>
                      <FormLabel>Win Probability (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          placeholder="10"
                          {...prizeField}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name={`prizes.${index}.isActive`}
                render={({ field: prizeField }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={prizeField.value}
                        onCheckedChange={(checked) =>
                          prizeField.onChange(Boolean(checked))
                        }
                      />
                    </FormControl>
                    <FormLabel>Active</FormLabel>
                  </FormItem>
                )}
              />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-orange-600 text-white hover:bg-orange-700"
          >
            {isSubmitting ? "Creating..." : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
